import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  MainSection,
} from '../common/styles';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../contexts/AuthContext';
import { CLUSTER_SCOPE, resolveMyClusterIds } from '../../utils/clusterScope';
import { BU_SCOPE, resolveMyBusinessUnitIds } from '../../utils/businessUnitScope';
import { feedbackApi } from '../../services/api';
import { skillsApi } from '../../services/api/skillsApi';
import useFeedbackSessionsStore from '../../store/feedbackSessionsStore';
import useIndividualAssignmentsStore from '../../store/individualAssignmentsStore';
import { FEEDBACK_SESSION_VIEW_MODES, getViewModeTitle } from '../../constants/feedbackSessionViewModes';
import BusinessUnitsTab from '../organization/Clusters/components/BusinessUnitsTab';
import TeamStructureView from './TeamStructureView';
import ClustersTreeView from './ClustersTreeView';
import AssignReviewer from './AssignReviewer';
import TypeSelection from './TypeSelection';
import SessionInformation from './SessionInformation';
import SessionTemplateSelection from './SessionTemplateSelection';

const STEPS = [
  'Session Information',
  'Select Cluster',
  'Select Review Type',
  'Select Session Template',
  'Select Business Unit',
  'Select Individuals',
  'Assign Reviewers'
];

// Steps for individual assignment mode
const INDIVIDUAL_STEPS = [
  'Select Review Type',
  'Select Session Template',
  'Select Individuals',
  'Assign Reviewers',
  'Deadline Date'
];

const ScheduleSession = ({ isIndividualMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const updateSession = useFeedbackSessionsStore((state) => state.updateSession);
  const fetchIndividualAssignments = useIndividualAssignmentsStore(
    (state) => state.fetchAssignmentsWithReset
  );
  const [activeStep, setActiveStep] = useState(0);
  const [sessionName, setSessionName] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [deadline, setDeadline] = useState('');
  const [allowEmployeeToAssignReviewers, setAllowEmployeeToAssignReviewers] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  // ADR-123 — Cluster scope for step 2. This is a view filter only: it
  // changes which cards are drawn and nothing about what may be scheduled.
  // Session-local by design — never persisted to the record, the URL or storage.
  const [clusterScope, setClusterScope] = useState(
    () => (resolveMyClusterIds(user?.employee).size ? CLUSTER_SCOPE.MINE : CLUSTER_SCOPE.ALL)
  );
  // The MINE projection reported back by ClustersTreeView once clusters load.
  const [mineSelectableIds, setMineSelectableIds] = useState(null);
  const clusterScopeInitialised = useRef(false);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  // ADR-125 — Business Unit scope for step 5. A view filter only, session-local, and
  // reset whenever the Cluster changes (Decision 5).
  const [buScope, setBuScope] = useState(
    () => (resolveMyBusinessUnitIds(user?.employee).size ? BU_SCOPE.MINE : BU_SCOPE.ALL)
  );
  // The MINE projection for the selected Cluster, reported by BusinessUnitsTab:
  // { selectableIds: Set|null, seedCount }. null until the tree has loaded.
  const [buMine, setBuMine] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [employeeAssignments, setEmployeeAssignments] = useState(new Map());
  const [reviewType, setReviewType] = useState(null);
  const [selectedSessionTemplate, setSelectedSessionTemplate] = useState(null);
  const [viewMode, setViewMode] = useState(FEEDBACK_SESSION_VIEW_MODES.EDIT);
  // Track if we're in new mode (creating a new session)
  const [isNewMode, setIsNewMode] = useState(true);
  // Track if we're scheduling individual assignments
  const [individualMode] = useState(isIndividualMode);

  useEffect(() => {
    if (location.state?.sessionData) {
      const { sessionData } = location.state;
      // Initialize form with session data
      setSessionName(sessionData.sessionInfo.name);
      setSelectedMonth(sessionData.sessionInfo.month);
      setSelectedYear(sessionData.sessionInfo.year);
      setDeadline(sessionData.sessionInfo.deadline);
      setAllowEmployeeToAssignReviewers(sessionData.sessionInfo.allowEmployeeToAssignReviewers || false);
      setReviewType(sessionData.reviewType);
      setSelectedSessionTemplate(sessionData.sessionTemplate);
      setSelectedCluster(sessionData.cluster);
      setSelectedBusinessUnit(sessionData.businessUnit);
      // Convert project objects to a Set of just the ID values using map
      const projectIds = new Set(
        (sessionData.sessionProjects || [])
          .filter(project => project && project.id)
          .map(project => project.id)
      );
      setSelectedProjects(projectIds);

      // Convert employee assignments array to Map
      const assignmentsMap = new Map();
      sessionData.employeeAssignments.forEach(assignment => {
        assignmentsMap.set(assignment.employeeId, {
          employee: { ...assignment.employee},
          assignment: {
            id: assignment.assignment.id,
            assignedAt: assignment.assignment.assignedAt,
            completedAt: assignment.assignment.completedAt,
          },
          matrix: assignment.matrix,
          reviewers: assignment.reviewers.map(reviewer => ({
            order: reviewer.reviewer.assignment.order,
            reviewType: reviewer.reviewer.assignment.reviewType,
            reviewer: { 
              id: reviewer.reviewer.id,
              fullName: reviewer.reviewer.fullName, 
              azureId: reviewer.reviewer.azureId,
              assignment: {
                assignedAt: reviewer.reviewer.assignment.assignedAt,
                completedAt: reviewer.reviewer.assignment.completedAt,
                startedAt: reviewer.reviewer.assignment.startedAt,
              }
            },
            template: reviewer.template
          }))
        });
      });
      setEmployeeAssignments(assignmentsMap);
      
      // Set isNewMode to false since we're editing an existing session
      setIsNewMode(false);
    }

    // Set view mode from location state if provided
    if (location.state?.viewMode) {
      setViewMode(location.state.viewMode);
      
      // Set initial active step based on view mode
      if (location.state.viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES) {
        setActiveStep(0); // Will be mapped to step 5 in the getVisibleSteps function
      } else if (location.state.viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS) {
        setActiveStep(0); // For EDIT_REVIEWERS, we only have one step
      }
    }

  }, [location.state]);

  useEffect(() => {
      // Only reset selected session template when review type changes if we're not editing an existing session
      if (!location.state?.sessionData) {
        setSelectedSessionTemplate(null);
      }
  }, [reviewType, location.state?.sessionData]);

  // Helper to navigate back to the list with scroll state preserved
  const navigateBackToList = (returnPath = '/feedback-sessions') => {
    const scrollState = location.state?.scrollState;
    navigate(returnPath, {
      state: {
        fromDetail: true,
        scrollState,
        needsRestore: true,
        hideContentUntilScrolled: true,
        timestamp: Date.now()
      }
    });
  };

  const handleNext = async () => {
    // Get the total number of steps for the current view mode
    const visibleSteps = getVisibleSteps();
    
    // For specialized view modes, determine if we're at the last step
    const isLastStep = 
      (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES && activeStep === 1) || 
      (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS && activeStep === 0) ||
      (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW && activeStep === visibleSteps.length - 1) ||
      (activeStep === (individualMode ? INDIVIDUAL_STEPS.length : STEPS.length) - 1);

    if (isLastStep) {
      // Only allow saving if not in view mode
      if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) {
        const returnPath = location.state?.returnPath || '/feedback-sessions';
        navigateBackToList(returnPath);
        return;
      }
      
      // For individual mode, create a different data structure
      if (individualMode) {
        const individualAssignmentData = {
          reviewType: reviewType,
          sessionTemplate: {
            id: selectedSessionTemplate.id,
          },
          deadline: deadline,
          employeeAssignments: Array.from(employeeAssignments.entries())
            .filter(([_, assignment]) => !assignment.deleted)
            .map(([employeeId, assignment]) => ({
              employeeId,
              // ADR-106 §2: identity by assignment id (absent on a new assignment → create)
              assignmentId: assignment.assignment?.id,
              matrix: {
                id: assignment.matrix.id,
                name: assignment.matrix.name
              },
              reviewers: assignment.reviewers.map(reviewer => ({
                reviewer: { id: reviewer.reviewer.id },
                template: {
                  id: reviewer.template.id,
                  description: reviewer.template.description,
                  type: reviewer.template.type
                }
              }))
            }))
        };
        
        try {
          // Call appropriate API endpoint based on whether we're editing or creating
          console.log('individualAssignmentData', individualAssignmentData);
          if (location.state?.sessionData) {
            // Update existing individual assignment
            await feedbackApi.updateIndividualAssignment(individualAssignmentData);
            // Refresh the assignments list in the store
            await fetchIndividualAssignments();
            showAlert('Individual assignment updated successfully', 'success');
          } else {
            // Create new individual assignment
            await feedbackApi.scheduleIndividualAssignments(individualAssignmentData);
            // Refresh the assignments list in the store
            await fetchIndividualAssignments();
            showAlert('Individual assignment scheduled successfully', 'success');
          }
          const defaultReturnPath = isIndividualMode ? '/individual-assignments' : '/feedback-sessions';
          const returnPath = location.state?.returnPath || defaultReturnPath;
          navigateBackToList(returnPath);
        } catch (error) {
          showAlert(error.message || `Failed to ${location.state?.sessionData ? 'update' : 'schedule'} individual assignment`, 'error', error);
        }
        return;
      }
      
      const sessionData = {
        sessionInfo: {
          name: sessionName,
          month: selectedMonth,
          year: selectedYear,
          deadline: deadline,
          allowEmployeeToAssignReviewers: allowEmployeeToAssignReviewers
        },
        reviewType: reviewType,
        sessionTemplate: {
          id: selectedSessionTemplate.id,
        },
        cluster: {
          id: selectedCluster?.id,
          name: selectedCluster?.name
        },
        businessUnit: {
          id: selectedBusinessUnit?.id,
          name: selectedBusinessUnit?.name,
          clusterId: selectedCluster?.id
        },
        projects: Array.from(selectedProjects),
        employeeAssignments: Array.from(employeeAssignments.entries()).map(([employeeId, assignment]) => ({
          employeeId,
          deleted: !!assignment.deleted,
          matrix: {
            id: assignment.matrix?.id,
            name: assignment.matrix?.name
          },
          reviewers: assignment.reviewers.map(reviewer => ({
            reviewer: { id: reviewer.reviewer.id },
            template: {
              id: reviewer.template.id,
              description: reviewer.template.description,
              type: reviewer.template.type
            }
          }))
        }))
      };

      // Transform API response format to match the list format expected by the store
      const transformSessionForStore = (apiSession) => {
        return {
          id: apiSession.id,
          name: apiSession.sessionInfo.name,
          month: apiSession.sessionInfo.month,
          year: apiSession.sessionInfo.year,
          deadlineAt: apiSession.sessionInfo.deadline,
          allowEmployeeToAssignReviewers: apiSession.sessionInfo.allowEmployeeToAssignReviewers,
          reviewType: apiSession.reviewType,
          sessionTemplate: apiSession.sessionTemplate,
          cluster: apiSession.cluster,
          businessUnit: apiSession.businessUnit,
          sessionProjects: apiSession.sessionProjects,
          assignments: apiSession.employeeAssignments?.map(ea => ({
            id: ea.assignment?.id,
            assignedAt: ea.assignment?.assignedAt,
            completedAt: ea.assignment?.completedAt,
            reviewsCompletedAt: ea.assignment?.reviewsCompletedAt,
            feedbackAttachedAt: ea.assignment?.feedbackAttachedAt,
            employee: ea.employee,
            matrix: ea.matrix,
            reviewers: ea.reviewers?.map(r => ({
              id: r.reviewer?.assignment?.id,
              employee: {
                id: r.reviewer?.id,
                fullName: r.reviewer?.fullName,
                email: r.reviewer?.email,
                azureId: r.reviewer?.azureId,
              },
              order: r.reviewer?.assignment?.order,
              assignedAt: r.reviewer?.assignment?.assignedAt,
              startedAt: r.reviewer?.assignment?.startedAt,
              completedAt: r.reviewer?.assignment?.completedAt,
              feedbackAttachedAt: r.reviewer?.assignment?.feedbackAttachedAt,
              reviewType: r.reviewer?.assignment?.reviewType,
              template: r.template,
            })) || [],
          })) || [],
        };
      };

      try {
        if (location.state?.sessionData) {
          // Update existing session
          const response = await feedbackApi.updateSession(location.state.sessionData.id, sessionData);
          updateSession(transformSessionForStore(response.session));
          showAlert('Feedback session updated successfully', 'success');
        } else {
          // Create new session
          const response = await feedbackApi.scheduleSession(sessionData);
          updateSession(transformSessionForStore(response.session));
          showAlert('Feedback session scheduled successfully', 'success');
        }
        const returnPath = location.state?.returnPath || '/feedback-sessions';
        navigateBackToList(returnPath);
      } catch (error) {
        showAlert(error.message || `Failed to ${location.state?.sessionData ? 'update' : 'schedule'} feedback session`, 'error', error);
        return;
      }
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0 ||
        (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS)) {
      // If at first step or in edit reviewers mode, go back to the return path or default to sessions list
      const returnPath = location.state?.returnPath || '/feedback-sessions';
      navigateBackToList(returnPath);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  // Handler for the header "Back to Individual Assignments" button - always goes to the list
  const handleBackToList = () => {
    const returnPath = location.state?.returnPath || '/feedback-sessions';
    navigateBackToList(returnPath);
  };

  // Handler for the stepper "Back" button - only does step navigation
  const handleStepBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  // ADR-123: stable identity — ClustersTreeView's d3 effect lists this in its
  // dependency array, and an inline arrow would rebuild the SVG (and re-issue
  // every avatar fetch) on each render of this 900-line component.
  const handleClusterSelect = useCallback((cluster) => {
    setSelectedCluster(cluster);
    setSelectedBusinessUnit(null);
    setEmployeeAssignments(new Map());
    // Reset selected projects when changing cluster
    setSelectedProjects(new Set());
  }, []);

  // ADR-123: the caller's own Clusters, from the `clusters` array that
  // GET /me already returns. Id-only — names come from the cluster payload.
  const myClusterIds = useMemo(() => resolveMyClusterIds(user?.employee), [user?.employee]);

  const handleScopeResolved = useCallback((resolved) => {
    setMineSelectableIds(resolved?.selectableIds ?? null);
  }, []);

  // Late-arriving auth (deep link into the wizard) still gets the scoped default.
  useEffect(() => {
    if (clusterScopeInitialised.current) return;
    if (!myClusterIds.size) return;
    clusterScopeInitialised.current = true;
    setClusterScope(CLUSTER_SCOPE.MINE);
  }, [myClusterIds]);

  // ADR-123 Invariant I3: a scope filter must never hide the current selection —
  // step 2's own validity gate reads state the operator could then no longer see.
  useEffect(() => {
    if (!selectedCluster || !mineSelectableIds) return;
    if (!mineSelectableIds.has(selectedCluster.id)) setClusterScope(CLUSTER_SCOPE.ALL);
  }, [selectedCluster, mineSelectableIds]);

  // Nothing to pick in VIEW mode, so a scope control would only be noise.
  useEffect(() => {
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) setClusterScope(CLUSTER_SCOPE.ALL);
  }, [viewMode]);

  const clusterScopeSwitchReason = useMemo(() => {
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) {
      return 'Scope cannot be changed while viewing a session.';
    }
    if (!myClusterIds.size) {
      return 'No Clusters are associated with your profile.';
    }
    if (selectedCluster && mineSelectableIds && !mineSelectableIds.has(selectedCluster.id)) {
      return `The selected Cluster (${selectedCluster.name}) is outside yours.`;
    }
    return null;
  }, [viewMode, myClusterIds, selectedCluster, mineSelectableIds]);

  const handleClusterScopeChange = (_event, next) => {
    // MUI emits null when the active button is clicked again.
    if (!next || next === clusterScope) return;
    setClusterScope(next);
  };

  // Add handler to reset selected projects when a business unit is selected.
  // ADR-125 Decision 8: stable identity.
  const handleBusinessUnitSelect = useCallback((businessUnit) => {
    setSelectedBusinessUnit(businessUnit);
    // Reset selected projects when changing business unit
    setSelectedProjects(new Set());
    // Reset all employee assignments when changing business unit
    setEmployeeAssignments(new Map());
  }, []);

  // ADR-125 Decision 2: member ∪ managed (with managed descendants), across all Clusters.
  // Narrowing to the selected Cluster happens in BusinessUnitsTab, by tree presence.
  const myBusinessUnitIds = useMemo(() => resolveMyBusinessUnitIds(user?.employee), [user?.employee]);
  const hasBusinessUnitSeeds = myBusinessUnitIds.size > 0;
  const selectedClusterId = selectedCluster?.id ?? null;

  const handleBuScopeResolved = useCallback((resolved) => {
    setBuMine(resolved ?? null);
  }, []);

  // ADR-125 Decision 5: scope is per Cluster — a new Cluster starts from the default.
  // Also covers late-arriving auth (deep link into the wizard).
  useEffect(() => {
    setBuMine(null);
    setBuScope(hasBusinessUnitSeeds ? BU_SCOPE.MINE : BU_SCOPE.ALL);
  }, [selectedClusterId, hasBusinessUnitSeeds]);

  // No seed in this Cluster: the tree already renders ALL; make the toggle say so.
  // ADR-125 Decision 6 / I3: never let the scope hide the current selection.
  useEffect(() => {
    if (!buMine) return;
    if (!buMine.selectableIds) {
      setBuScope(BU_SCOPE.ALL);
      return;
    }
    if (selectedBusinessUnit && !buMine.selectableIds.has(selectedBusinessUnit.id)) {
      setBuScope(BU_SCOPE.ALL);
    }
  }, [buMine, selectedBusinessUnit]);

  useEffect(() => {
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) setBuScope(BU_SCOPE.ALL);
  }, [viewMode]);

  // Precedence (ADR-125 Decision 6): VIEW → selection outside Mine → nothing of yours here.
  const buScopeSwitchReason = useMemo(() => {
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) {
      return 'Scope cannot be changed while viewing a session.';
    }
    if (selectedBusinessUnit && buMine?.selectableIds && !buMine.selectableIds.has(selectedBusinessUnit.id)) {
      return `The selected Business Unit (${selectedBusinessUnit.name}) is outside yours.`;
    }
    if (!hasBusinessUnitSeeds || (buMine && !buMine.selectableIds)) {
      return 'None of your Business Units are in this Cluster.';
    }
    return null;
  }, [viewMode, selectedBusinessUnit, buMine, hasBusinessUnitSeeds]);

  const handleBuScopeChange = (_event, next) => {
    // MUI emits null when the active button is clicked again.
    if (!next || next === buScope) return;
    setBuScope(next);
  };

  const handleEmployeeSelection = (employeeId, employee, matrix) => {
    setEmployeeAssignments(prev => {
      const next = new Map(prev);
      if (matrix) {
        next.set(employeeId, {
          employee,
          matrix,
          reviewers: prev.get(employeeId)?.reviewers || []
        });
      } else {
        // Instead of deleting the entry, mark it as deleted
        const currentAssignment = prev.get(employeeId);
        if (currentAssignment) {
          next.set(employeeId, {
            ...currentAssignment,
            matrix: null,
            reviewers: [],
            deleted: true
          });
        }
      }
      return next;
    });
  };

  const handleReviewerAssignment = (employeeId, reviewerAssignments) => {
    setEmployeeAssignments(prev => {
      const next = new Map(prev);
      const currentAssignment = prev.get(employeeId);
      if (!currentAssignment) return next;

      if (reviewerAssignments.length === 0) {
        next.set(employeeId, {
          ...currentAssignment,
          reviewers: []
        });
      } else {
        next.set(employeeId, {
          ...currentAssignment,
          reviewers: reviewerAssignments
        });
      }
      return next;
    });
  };

  // Add handler for project selection
  const handleProjectSelection = (projects) => {
    setSelectedProjects(projects);
  };

  // Get visible steps based on view mode
  const getVisibleSteps = () => {
    // Use individual steps if in individual mode
    const steps = individualMode ? INDIVIDUAL_STEPS : STEPS;
    
    switch (viewMode) {
      case FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES:
        return individualMode ? [INDIVIDUAL_STEPS[2], INDIVIDUAL_STEPS[3]] : [STEPS[5], STEPS[6]]; // Only show "Select Employees" and "Assign Reviewers" steps
      case FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS:
        return individualMode ? [INDIVIDUAL_STEPS[3]] : [STEPS[6]]; // Only show "Assign Reviewers" step
      case FEEDBACK_SESSION_VIEW_MODES.VIEW:
        return steps; // Show all steps in view mode
      default:
        return steps;
    }
  };

  // Handle clicking directly on a step
  const handleStepClick = (index) => {
    // The isStepClickable function already handles all the validation logic
    // This function will only be called if the step is clickable
    setActiveStep(index);
  };

  // Determine if a step is clickable
  const isStepClickable = (index) => {
    // Not clickable in new mode
    if (isNewMode) {
      return false;
    }
    
    // Always clickable in view mode
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) {
      return true;
    }
    
    // Always allow backward navigation
    if (index <= activeStep) {
      return true;
    }
    
    // For specialized view modes
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES || 
        viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS) {
      return true;
    }
    
    // For regular edit mode, check if all previous steps are valid
    let canNavigate = true;
    
    for (let i = 0; i < index; i++) {
      switch (i) {
        case 0:
          if (!sessionName || !selectedMonth || !selectedYear || !deadline) {
            canNavigate = false;
          }
          break;
        case 1:
          if (!selectedCluster) {
            canNavigate = false;
          }
          break;
        case 2:
          if (!reviewType) {
            canNavigate = false;
          }
          break;
        case 3:
          if (!selectedSessionTemplate) {
            canNavigate = false;
          }
          break;
        case 4:
          if (!selectedBusinessUnit) {
            canNavigate = false;
          }
          break;
        default:
          break;
      }
      
      if (!canNavigate) {
        break;
      }
    }
    
    return canNavigate;
  };

  const renderStepContent = (step) => {
    // For individual mode, map steps differently
    let actualStep = step;
    
    // Handle view mode mappings first, regardless of individual mode
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES) {
      // Map step 0 to content 5 (Select Employees), step 1 to content 6 (Assign Reviewers)
      actualStep = step === 0 ? 5 : 6;
    } else if (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS) {
      // Always show content 6 for EDIT_REVIEWERS
      actualStep = 6;
    } else if (individualMode) {
      // Map individual steps to the appropriate content (only for full flow, not view modes)
      const stepMapping = {
        0: 2,  // Select Review Type
        1: 3,  // Select Session Template
        2: 5,  // Select Employees
        3: 6,  // Assign Reviewers
        4: 'deadline'  // Deadline Date (custom step)
      };
      actualStep = stepMapping[step] !== undefined ? stepMapping[step] : step;
    }
    
    switch (actualStep) {
      case 0:
        return (
          <SessionInformation
            sessionName={sessionName}
            onSessionNameChange={setSessionName}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            deadline={deadline}
            onDeadlineChange={setDeadline}
            allowEmployeeToAssignReviewers={allowEmployeeToAssignReviewers}
            onAllowEmployeeToAssignReviewersChange={setAllowEmployeeToAssignReviewers}
            readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW}
          />
        );
      case 1:
        return (
          <Box sx={{ mt: 4 }}>
            {/* ADR-123: scope toggle, mirroring the ADR-118 MoveBusinessUnitModal header row. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW ? 'Selected cluster:' : 'Select a cluster from the organization structure:'}
              </Typography>
              <Tooltip
                title={clusterScopeSwitchReason || ''}
                disableHoverListener={!clusterScopeSwitchReason}
              >
                <span>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={clusterScope}
                    onChange={handleClusterScopeChange}
                    disabled={Boolean(clusterScopeSwitchReason)}
                    aria-label="Cluster scope"
                  >
                    <ToggleButton value={CLUSTER_SCOPE.MINE} aria-label="my clusters">
                      Mine{myClusterIds.size ? ` (${myClusterIds.size})` : ''}
                    </ToggleButton>
                    <ToggleButton value={CLUSTER_SCOPE.ALL} aria-label="all clusters">
                      All
                    </ToggleButton>
                  </ToggleButtonGroup>
                </span>
              </Tooltip>
            </Box>
            <ClustersTreeView 
              onSelectCluster={handleClusterSelect}
              initialCluster={selectedCluster}
              readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW}
              myClusterIds={myClusterIds}
              scope={clusterScope}
              onScopeResolved={handleScopeResolved}
            />
            {/* ADR-123 Decision 5: the fallback to All is stated, never silent. */}
            {!myClusterIds.size && viewMode !== FEEDBACK_SESSION_VIEW_MODES.VIEW && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748B' }}>
                No Clusters are associated with your profile — showing all.
              </Typography>
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 4 }}>
            <TypeSelection
              selectedType={reviewType}
              onTypeChange={setReviewType}
              readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW}
            />
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW ? 'Selected session template:' : 'Select a session template:'}
            </Typography>
            <SessionTemplateSelection
              clusterId={individualMode ? null : selectedCluster?.id}
              reviewType={reviewType}
              selectedTemplate={selectedSessionTemplate}
              onTemplateSelect={setSelectedSessionTemplate}
              onTemplateChange={() => {
                // Reset employees and reviewers when template changes in new/edit mode
                if (viewMode !== FEEDBACK_SESSION_VIEW_MODES.VIEW) {
                  // Mark all existing employee assignments as deleted instead of clearing them
                  const updatedAssignments = new Map();
                  employeeAssignments.forEach((assignment, employeeId) => {
                    updatedAssignments.set(employeeId, {
                      ...assignment,
                      deleted: true,
                      reviewers: [], // Clear reviewers when assignment is marked as deleted
                      matrix: null // Clear skills matrix when assignment is marked as deleted
                    });
                  });
                  setEmployeeAssignments(updatedAssignments);
                  setSelectedProjects(new Set());
                }
              }}
              readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW}
            />
          </Box>
        );
      case 4:
        return (
          <Box sx={{ mt: 4 }}>
            {/* ADR-125: scope toggle — same header row as the ADR-123 Cluster step. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW ? 'Selected business unit:' : 'Select a business unit from the tree structure:'}
              </Typography>
              <Tooltip
                title={buScopeSwitchReason || ''}
                disableHoverListener={!buScopeSwitchReason}
              >
                <span>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={buScope}
                    onChange={handleBuScopeChange}
                    disabled={Boolean(buScopeSwitchReason)}
                    aria-label="Business Unit scope"
                  >
                    <ToggleButton value={BU_SCOPE.MINE} aria-label="my business units">
                      {/* Seeds in THIS Cluster, not the global count (Decision 7). */}
                      Mine{buMine?.seedCount ? ` (${buMine.seedCount})` : ''}
                    </ToggleButton>
                    <ToggleButton value={BU_SCOPE.ALL} aria-label="all business units">
                      All
                    </ToggleButton>
                  </ToggleButtonGroup>
                </span>
              </Tooltip>
            </Box>
            {/* ADR-125 Decision 5: the fallback to All is stated, never silent. */}
            {viewMode !== FEEDBACK_SESSION_VIEW_MODES.VIEW
              && (!hasBusinessUnitSeeds || (buMine && !buMine.selectableIds)) && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748B' }}>
                None of your Business Units are in {selectedCluster?.name || 'this Cluster'} — showing all.
              </Typography>
            )}
            <BusinessUnitsTab 
              clusterId={selectedCluster.id} 
              onSelectBusinessUnit={handleBusinessUnitSelect}
              viewOnly
              initialBusinessUnit={selectedBusinessUnit}
              readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW}
              hideInactiveToggle
              myBusinessUnitIds={myBusinessUnitIds}
              scope={buScope}
              onScopeResolved={handleBuScopeResolved}
              currentEmployeeId={user?.employee?.id ?? null}
            />
          </Box>
        );
      case 5:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW ? 'Selected individuals:' : 'Select individuals to attach to the feedback session:'}
            </Typography>
            <TeamStructureView 
              businessUnit={selectedBusinessUnit}
              onEmployeeSelection={handleEmployeeSelection}
              employeeAssignments={employeeAssignments}
              reviewType={reviewType}
              readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW || (individualMode && !isNewMode)}
              onReviewerAssignment={handleReviewerAssignment}
              selectedProjects={selectedProjects}
              onProjectSelection={handleProjectSelection}
              selectedSessionTemplate={selectedSessionTemplate}
              isIndividualMode={individualMode}
            />
          </Box>
        );
      case 6:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW ? 'Assigned reviewers:' : 'Assign reviewers to selected individuals:'}
            </Typography>
            <AssignReviewer
              businessUnit={selectedBusinessUnit}
              employeeAssignments={employeeAssignments}
              onReviewerAssignment={handleReviewerAssignment}
              reviewType={reviewType}
              readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW}
              selectedProjects={selectedProjects}
              selectedSessionTemplate={selectedSessionTemplate}
              isIndividualMode={individualMode}
            />
          </Box>
        );
      case 'deadline':
        return (
          <SessionInformation
            sessionName={sessionName}
            onSessionNameChange={setSessionName}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            deadline={deadline}
            onDeadlineChange={setDeadline}
            allowEmployeeToAssignReviewers={allowEmployeeToAssignReviewers}
            onAllowEmployeeToAssignReviewersChange={setAllowEmployeeToAssignReviewers}
            readOnly={viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW}
            showOnlyDeadline={true}
          />
        );
      default:
        return null;
    }
  };

  // Helper function to check if there are any valid employee assignments
  const hasValidEmployeeAssignments = () => {
    // Check if there are any non-deleted employee assignments
    for (const assignment of employeeAssignments.values()) {
      if (!assignment.deleted) {
        return true;
      }
    }
    return false;
  };

  // Helper function to check if scheduling conditions are met:
  // Either valid employee assignments OR selected projects
  const canScheduleSession = () => {
    return hasValidEmployeeAssignments() || (selectedProjects && selectedProjects.size > 0);
  };

  const isNextDisabled = () => {
    // In view mode, never disable the next button
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) {
      return false;
    }
    
    // For individual mode, validate based on the step
    if (individualMode) {
      switch (activeStep) {
        case 0: // Select Review Type
          return !reviewType;
        case 1: // Select Session Template
          return !selectedSessionTemplate;
        case 2: // Select Employees
          return false;
        case 3: // Assign Reviewers
          return !hasValidEmployeeAssignments();
        case 4: // Deadline Date
          return !deadline;
        default:
          return false;
      }
    }
    
    // For specialized view modes, map the step index to the appropriate validation
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES) {
      // Disable Schedule/Update button on the last step if no valid employee assignments or projects
      if (activeStep === 1) {
        return !canScheduleSession();
      }
      return false;
    }
    
    if (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS) {
      // Disable Schedule/Update button if no valid employee assignments or projects
      return !canScheduleSession();
    }
    
    // For regular view mode, validate based on the step
    switch (activeStep) {
      case 0:
        return !sessionName || !selectedMonth || !selectedYear || !deadline;
      case 1:
        return !selectedCluster;
      case 2:
        return !reviewType;
      case 3:
        return !selectedSessionTemplate;
      case 4:
        return !selectedBusinessUnit;
      case 5:
        return false;
      case 6:
        // On the final step, disable the button if no employees are assigned AND no projects selected
        return !canScheduleSession();
      default:
        return false;
    }
  };

  // Determine the title based on view mode
  const getTitle = () => {
    if (individualMode) {
      return 'Schedule Individual Assignment';
    }
    if (location.state?.sessionData) {
      return getViewModeTitle(viewMode);
    }
    return 'Schedule Feedback Session';
  };

  // Determine the next button text based on view mode and step
  const getNextButtonText = () => {
    // Get the total number of steps for the current view mode
    const visibleSteps = getVisibleSteps();
    
    // For specialized view modes, determine if we're at the last step
    const isLastStep = 
      (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES && activeStep === 1) || 
      (viewMode === FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS && activeStep === 0) ||
      (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW && activeStep === visibleSteps.length - 1) ||
      (activeStep === (individualMode ? INDIVIDUAL_STEPS.length : STEPS.length) - 1);

    if (isLastStep) {
      if (viewMode === FEEDBACK_SESSION_VIEW_MODES.VIEW) {
        return 'Done';
      }
      if (individualMode) {
        return 'Schedule Assignment';
      }
      return location.state?.sessionData ? 'Update' : 'Schedule';
    }
    return 'Next';
  };

  // Determine the back button text based on return path
  const getBackButtonText = () => {
    const returnPath = location.state?.returnPath;
    if (returnPath === '/individual-assignments') {
      return 'Back to Individual Assignments';
    }
    return 'Back to Feedback Sessions';
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBackToList}>
            <FaArrowLeft /> {getBackButtonText()}
          </BackButton>
          <Title>{getTitle()}</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {getVisibleSteps().map((label, index) => {
              const clickable = isStepClickable(index);
              return (
                <Step 
                  key={label} 
                  onClick={() => clickable && handleStepClick(index)}
                  sx={{ 
                    cursor: clickable ? 'pointer' : 'default',
                    '&:hover': clickable ? { 
                      '& .MuiStepLabel-label': { 
                        color: '#2563EB' 
                      } 
                    } : {},
                    opacity: clickable || index === activeStep ? 1 : 0.7,
                  }}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
            {activeStep > 0 && (
              <Button
                onClick={handleStepBack}
                variant="outlined"
                sx={{
                  borderColor: '#E2E8F0',
                  color: '#475569',
                  '&:hover': {
                    borderColor: '#CBD5E1',
                    backgroundColor: '#F8FAFC',
                  },
                }}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={isNextDisabled()}
              sx={{
                backgroundColor: '#2563EB',
                '&:hover': {
                  backgroundColor: '#1D4ED8',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E2E8F0',
                  color: '#94A3B8',
                },
              }}
            >
              {getNextButtonText()}
            </Button>
          </Box>
        </MainSection>
      </Content>
    </Container>
  );
};

export default ScheduleSession; 